import React, { useState, useEffect, useRef } from "react";
import { useLazyQuery } from "@apollo/react-hooks";
import {
  Typography,
  Box,
  Grid,
  makeStyles,
  Paper,
  IconButton,
  SvgIcon,
  Divider
} from "@material-ui/core";
import {
  FormActionDrawer,
  Loader,
  LocationCardCompact,
  MapboxMap,
  Carousel
} from "../../../components";
import { useHeader } from "../../../contexts";
import { GGV_GET_LOCATIONS_BY_DATE } from "../../../queries";
import { Marker } from "react-map-gl";
import { ReactComponent as LocationPinIcon } from "../../../images/location-pin.svg";
import Geocoding from "@mapbox/mapbox-sdk/services/geocoding";
import {
  CONSUMER_SESSION_LOCATION,
  COUNTRIES_ISO_CODES
} from "../../../constants";
import { useTranslation } from "react-i18next";
import moment from "moment";

const useClasses = makeStyles(theme => ({
  mapContainer: {
    position: "relative",
    height: "150px"
  },
  iconButton: {
    position: "relative"
  },
  iconOrder: {
    position: "absolute",
    left: "50%",
    transform: "translate(-50%, -90%)",
    top: "50%",
    fontSize: "12px",
    fontWeight: 700,
    color: theme.palette.common.black
  },
  locationPin: {
    fill: theme.palette.primary.main
  }
}));

const DEFAULT_MAP_CONFIG = {
  latitude: 32.779167,
  longitude: -96.808891,
  zoom: 8
};

const getCurrentPosition = options =>
  new Promise((res, rej) =>
    navigator.geolocation.getCurrentPosition(res, rej, options)
  );

const ScheduleLocationByDate = ({
  setDrawerClearance,
  updateScheduleData,
  scheduleData,
  isSubmitting
}) => {
  const { t } = useTranslation();
  const classes = useClasses();
  const { setTitle } = useHeader();
  const [selectedLocation, setSelectedLocation] = useState();
  const [preselectedLocation, setPreselectedLocation] = useState();
  const [error, setError] = useState(false);
  const [mapConfig, setMapConfig] = useState();
  const [locations, setLocations] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [currentDateIndex, setCurrentDateIndex] = useState();
  const [locationsError, setLocationsError] = useState(false);
  const locationCardRefs = useRef([]);

  const geocodingClient = Geocoding({
    accessToken: process.env.REACT_APP_MAPBOX_ACCESS_TOKEN
  });

  useEffect(() => {
    setTitle(t("schedule.location.title"));
  }, []);

  /**
   * Set map config to user location on mount
   */
  useEffect(() => {
    if (scheduleData.patientAddress) {
      geocodingClient
        .forwardGeocode({
          query: Object.values(scheduleData.patientAddress).join(","),
          limit: 1,
          countries: [COUNTRIES_ISO_CODES.US, COUNTRIES_ISO_CODES.MX]
        })
        .send()
        .then(response => {
          const { body } = response;
          const { features = [] } = body;
          const [location] = features;

          if (location && Array.isArray(location.center)) {
            const [lng, lat] = location.center;

            setMapConfig({
              ...DEFAULT_MAP_CONFIG,
              latitude: lat,
              longitude: lng
            });
          }
        });
    } else {
      getCurrentPosition({ maximumAge: 0, timeout: 5000 })
        .then(({ coords }) => {
          setMapConfig({
            ...DEFAULT_MAP_CONFIG,
            latitude: coords.latitude,
            longitude: coords.longitude
          });
        })
        .catch(() => setMapConfig(DEFAULT_MAP_CONFIG));
    }
  }, []);

  /**
   * Get location by id
   *
   * @param {number} id
   * @param {array} locations
   */
  const getLocationById = (id, locations) => {
    const location = locations.filter(location => location.id === parseInt(id));

    return location.length > 0 ? location[0] : {};
  };

  /**
   * Set location if we already have one
   */
  useEffect(() => {
    if (scheduleData.location) setSelectedLocation(scheduleData.location);
  }, [scheduleData]);

  /**
   * Set location id from session
   */
  useEffect(() => {
    const savedLocation = sessionStorage.getItem(CONSUMER_SESSION_LOCATION);

    if (!locations || !savedLocation) return;

    const location = getLocationById(savedLocation, locations);

    if (location.id) {
      setPreselectedLocation(location);
      setSelectedLocation(location);
    }
  }, [locations]);

  useEffect(() => {
    if (!availableDates) return;

    setCurrentDateIndex(0);
  }, [availableDates]);

  /**
   * Get locations from API
   */
  const [
    getLocationsByDate,
    { loading, error: getLocationsError }
  ] = useLazyQuery(GGV_GET_LOCATIONS_BY_DATE, {
    onCompleted: ({ locationsByDate }) => {
      if (
        locationsByDate &&
        locationsByDate.status &&
        locationsByDate.status === "success" &&
        locationsByDate.results &&
        Array.isArray(locationsByDate.results.locations) &&
        Array.isArray(locationsByDate.results.dates)
      ) {
        const {
          results: { locations, dates }
        } = locationsByDate;

        setLocations(locations);
        setAvailableDates(dates);
      } else {
        setLocationsError(true);
      }
    },
    onError: () => setLocationsError(true)
  });

  /**
   * Get locations when map config changes
   */
  useEffect(() => {
    if (!mapConfig) return;

    getLocationsByDate({
      variables: {
        groupCode: scheduleData.groupCode,
        longitude: mapConfig.longitude,
        latitude: mapConfig.latitude,
        distance: 5000
      }
    });
  }, [mapConfig, getLocationsByDate]);

  const handleChange = location => {
    setSelectedLocation(location);
  };

  const handleClickPin = index => {
    locationCardRefs.current[index].scrollIntoView({
      behavior: "smooth",
      block: "center"
    });
  };

  /**
   * Handle Prev Date Button
   */
  const handlePrevDate = () => {
    setCurrentDateIndex(prev => (prev - 1 > 0 ? prev - 1 : 0));
  };

  /**
   * Handle Next Date Button
   */
  const handleNextDate = () => {
    setCurrentDateIndex(prev =>
      prev + 1 <= availableDates.length ? prev + 1 : prev
    );
  };

  /**
   * Get next available date for dose 2
   *
   * @param {number} locationId
   * @param {object} date - chosen date for dose one
   */
  const getNextAvailableDateDose2 = (locationId, date) => {
    let nextDate = "";

    const [location] = date.locations.filter(
      location => location.id === locationId
    );

    if (
      location &&
      Array.isArray(location.next_available_dates) &&
      location.next_available_dates.length > 0
    ) {
      nextDate = location.next_available_dates[0];
    }

    return nextDate;
  };

  /**
   * Handle Next Button
   */
  const handleNext = () => {
    if (selectedLocation && selectedLocation.id) {
      // Remove previously stored
      sessionStorage.removeItem(CONSUMER_SESSION_LOCATION);

      updateScheduleData({
        location: selectedLocation.id,
        locationDetails: selectedLocation,
        locationServices: selectedLocation.vax_type || "",
        date: availableDates[currentDateIndex].date,
        nextAvailableDate: getNextAvailableDateDose2(
          selectedLocation.id,
          availableDates[currentDateIndex]
        ),
        collectInsurance:
          "collect_insurance_info" in selectedLocation &&
          selectedLocation.collect_insurance_info === false
            ? false
            : true,
        singleDoseVax:
          selectedLocation.vax_type && selectedLocation.vax_type === "JNJ"
            ? true
            : false,
        appointmentTwoTime: 0 // Default if there is no apppointment two time
      });
    } else {
      setError(true);
    }
  };

  const getLocationDetail = location => {
    const [locationDetail] = locations.filter(
      locationDetail => locationDetail.id === location.id
    );

    return locationDetail ? { ...location, ...locationDetail } : location;
  };

  if (
    loading ||
    !mapConfig ||
    (currentDateIndex === undefined && !locationsError)
  )
    return <Loader />;

  if (getLocationsError)
    return (
      <Typography color="error">{t("schedule.location.error_api")}</Typography>
    );

  return (
    <Box data-cy="schedule-screen-location-by-date">
      {error && (
        <Typography
          paragraph
          data-cy="schedule-screen-location-by-date-error"
          color="error"
        >
          {t("schedule.location.error")}
        </Typography>
      )}
      {(locationsError || !availableDates.length) && (
        <Typography>{t("schedule.location.error_empty")}</Typography>
      )}
      {availableDates.length > 0 && (
        <Grid container direction="row">
          <Grid item xs={12}>
            <Paper>
              <Box p={1}>
                <Carousel
                  onPrev={handlePrevDate}
                  onNext={handleNextDate}
                  currentIndex={currentDateIndex}
                  totalItems={availableDates.length}
                >
                  <Typography align="center" variant="body2">
                    <strong>
                      {moment(
                        availableDates[currentDateIndex].date,
                        "YYYY-MM-DD"
                      ).format("dddd, MMM Do, YYYY")}
                    </strong>
                  </Typography>
                </Carousel>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} className={classes.mapContainer}>
            <MapboxMap {...mapConfig} scrollZoom={false}>
              {availableDates[currentDateIndex].locations.map(
                (location, index) => {
                  const { lat, lng } = getLocationDetail(location);

                  return (
                    <Marker
                      key={`marker-location-${location.id}`}
                      latitude={lat}
                      longitude={lng}
                      offsetTop={-24}
                      offsetLeft={-28}
                    >
                      <IconButton
                        className={classes.iconButton}
                        onClick={() => handleClickPin(index)}
                      >
                        <SvgIcon fontSize="large">
                          <LocationPinIcon className={classes.locationPin} />
                        </SvgIcon>
                        <Box className={classes.iconOrder}>{index + 1}</Box>
                      </IconButton>
                    </Marker>
                  );
                }
              )}
            </MapboxMap>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {preselectedLocation && (
                <Grid
                  item
                  xs={12}
                  data-cy="schedule-screen-location-preselected"
                >
                  <LocationCardCompact
                    location={preselectedLocation}
                    selectedLocation={preselectedLocation}
                    handleSelect={handleChange}
                    showSelectedAlert={true}
                  />
                  <Box mt={2}>
                    <Typography align="center" variant="h6" gutterBottom>
                      <strong>
                        {t("schedule.location.preselected.title")}
                      </strong>
                    </Typography>
                    <Divider />
                  </Box>
                </Grid>
              )}
              {availableDates[currentDateIndex].locations.map(
                (location, index) => (
                  <Grid
                    key={location.id}
                    item
                    xs={12}
                    ref={cardRef => (locationCardRefs.current[index] = cardRef)}
                  >
                    <LocationCardCompact
                      location={getLocationDetail(location)}
                      selectedLocation={selectedLocation}
                      handleSelect={handleChange}
                      order={index + 1}
                    />
                  </Grid>
                )
              )}
            </Grid>
          </Grid>
        </Grid>
      )}
      {selectedLocation && (
        <FormActionDrawer
          setDrawerClearance={setDrawerClearance}
          handleNext={() => handleNext()}
          loading={isSubmitting}
        />
      )}
    </Box>
  );
};

export { ScheduleLocationByDate };
